import { useCallback, useRef } from 'react';

import type { FotoTemporalDraft } from '@/features/analisis/common/fotosTypes';
import type { HpgFormValues, HpgMuestraFormValues } from '../schemas';
import type {
  ComplementarioState,
  SedFhComplementarioState,
  TipoComplementario,
} from '../components/hpgDialogTypes';

const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const DRAFT_DEBOUNCE_MS = 1000;

export interface HpgDraftFormValues {
  fecha_analisis: string;
  identificacion_muestras: string | null;
  alimentacion: string | null;
  ultimo_antiparasitario: string | null;
  fecha_ultimo_tratamiento: string | null;
  comentarios_lote: string | null;
  muestras: HpgMuestraFormValues[];
}

export type SedFhComplementarioDraft = Omit<SedFhComplementarioState, 'fotosTemporales'> & {
  fotosTemporales: FotoTemporalDraft[];
};

export type ComplementarioDraft =
  | Exclude<ComplementarioState, SedFhComplementarioState>
  | SedFhComplementarioDraft;

export interface HpgDraft {
  uidProtocolo: string;
  timestamp: number;
  formValues: HpgDraftFormValues;
  complementarios: ComplementarioDraft[];
  tipoComplementario: TipoComplementario | null;
}

function draftKey(uidProtocolo: string): string {
  return `hpg-draft-${uidProtocolo}`;
}

function serializeFotos(comps: ComplementarioState[]): ComplementarioDraft[] {
  return comps.map((c) => {
    if (c.tipo !== 'sedimentacion_fh') return c;
    const sedFh = c as SedFhComplementarioState;
    return {
      ...sedFh,
      fotosTemporales: sedFh.fotosTemporales
        .filter((f) => f.uploadStatus === 'done' && f.uid_foto !== '')
        .map((f): FotoTemporalDraft => ({
          localId: f.localId,
          uid_foto: f.uid_foto,
          content_type: f.file.type,
          caption: f.caption,
        })),
    };
  });
}

export function useHpgDraft() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveDraft = useCallback(
    (
      uidProtocolo: string,
      formValues: HpgFormValues,
      complementarios: ComplementarioState[],
      tipoComplementario: TipoComplementario | null,
    ): void => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const draft: HpgDraft = {
          uidProtocolo,
          timestamp: Date.now(),
          formValues: {
            ...formValues,
            fecha_analisis: formValues.fecha_analisis.toISOString(),
            fecha_ultimo_tratamiento:
              formValues.fecha_ultimo_tratamiento?.toISOString() ?? null,
          },
          complementarios: serializeFotos(complementarios),
          tipoComplementario,
        };
        try {
          localStorage.setItem(draftKey(uidProtocolo), JSON.stringify(draft));
        } catch {
          // quota exceeded or storage unavailable
        }
      }, DRAFT_DEBOUNCE_MS);
    },
    [],
  );

  const loadDraft = useCallback((uidProtocolo: string): HpgDraft | null => {
    try {
      const raw = localStorage.getItem(draftKey(uidProtocolo));
      if (!raw) return null;
      const draft = JSON.parse(raw) as HpgDraft;
      if (Date.now() - draft.timestamp > DRAFT_TTL_MS) {
        localStorage.removeItem(draftKey(uidProtocolo));
        return null;
      }
      return draft;
    } catch {
      return null;
    }
  }, []);

  const clearDraft = useCallback((uidProtocolo: string): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    try {
      localStorage.removeItem(draftKey(uidProtocolo));
    } catch {
      // silently ignore
    }
  }, []);

  return { saveDraft, loadDraft, clearDraft };
}
